import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';

import { translation } from '@cue/translations';
import { ReactiveFormsModule } from '@angular/forms';

import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { TextBoxModule } from '@progress/kendo-angular-inputs';
import { ExpansionPanelModule } from '@progress/kendo-angular-layout';
import { API_BASEURL, OpenDetailFromEnum } from '@cue/api';
import {
  cueArrowsUpDown,
  cueClose,
  cueEdit,
  cueMap,
  cueQrCode,
  cueStart,
  cueTarget,
  cueWarning,
  IconComponent,
} from '@cue/ui/icons';
import { ResourceDetailComponent } from '../resource-detail/resource-detail.component';
import {
  NavigationService,
  PathInfoResultEnum,
  PathResultCrate,
  StartTargetResultEnum,
} from '@cue/navigation-services';
import { LocalizedStringPipe } from '@cue/utilities';
import { AreaUnavailableNotice, NavigationStateEnum, ResourcePoiIdentificationCrate, RouteStateEnum } from '@cue/data';
import { rxResource } from '@angular/core/rxjs-interop';
import { ResponsiveService } from '@cue/responsivity';
import {
  NavigationRouteOverviewComponent,
  RouteOverviewNote,
  RouteOverviewNoteSelection,
  SpecificAreaDataCrate,
} from '../navigation-route-overview/navigation-route-overview.component';
import { NavigationBarDataV2 } from '../../pipes/navigation-bar.pipe';

export interface IResourceIdentification {
  resourceId: number;
  qrCodeId: string;
}

@Component({
  selector: 'cue-resources-detail',
  templateUrl: './resources-detail.component.html',
  styleUrls: ['./resources-detail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ExpansionPanelModule,
    TranslocoPipe,
    ReactiveFormsModule,
    TextBoxModule,
    IconComponent,
    ResourceDetailComponent,
    LocalizedStringPipe,
    NavigationRouteOverviewComponent,
  ],
  animations: [
    trigger('slideInTop', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-50%)' }),
        animate('300ms', style({ opacity: 1, transform: 'translateY(0%)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translateY(0%)' }),
        animate('300ms', style({ opacity: 0, transform: 'translateY(-50%)' })),
      ]),
    ]),
    trigger('opacity', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms', style({ opacity: 0 }))]),
    ]),
    trigger('scale', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scale(0)' }),
        animate(
          '300ms',
          style({
            opacity: 1,
            transform: 'scale(1)',
          })
        ),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'scale(1)' }),
        animate(
          '300ms',
          style({
            opacity: 0,
            transform: 'scale(0)',
          })
        ),
      ]),
    ]),
  ],
})
export class ResourcesDetailComponent {
  protected apiUrl = inject<string>(API_BASEURL);
  private navigationService = inject(NavigationService);
  private translocoService = inject(TranslocoService);
  navigationStateEnum = NavigationStateEnum;
  translation = translation;

  protected startTargetResultEnum = StartTargetResultEnum;

  ICONS = {
    cueEdit,
    cueTarget,
    cueStart,
    cueWarning,
    cueArrowsUpDown,
    cueClose,
    cueQrCode,
    cueMap,
  };

  isKiosk = input<boolean>();
  areaUnavailableNotice = input<AreaUnavailableNotice | null>(null);
  startResource = model<IResourceIdentification>();
  targetResource = model<IResourceIdentification>();

  hasStart = computed(() => this.startResource()?.resourceId || !!this.startResource()?.qrCodeId);
  hasTarget = computed(() => this.targetResource()?.resourceId || !!this.targetResource()?.qrCodeId);

  targetResourceCategoryName = input<string>();
  areaId = input<number>();
  groupIdx = input.required<number>();
  routeFindStatus = input<RouteStateEnum>();
  routeSearchResult = input<PathResultCrate>();
  navBarData = input<NavigationBarDataV2 | null>();
  routeOverviewNotes = input<Record<number, RouteOverviewNote[]>>({});
  requestOpenMapItemDetail = output<ResourcePoiIdentificationCrate>();
  requestSwitchStartTarget = output<void>();
  requestDeleteStart = output<void>();
  requestDeleteTarget = output<void>();
  requestMoveToSpecificArea = output<SpecificAreaDataCrate>();
  routeOverviewNoteClick = output<RouteOverviewNoteSelection>();
  wantChangeState = output<NavigationStateEnum>();
  scanStartSelection = output();
  responsiveService = inject(ResponsiveService);
  private hasInitializedAreaUnavailableNotice = false;
  private readonly notifyAreaUnavailable = effect(() => {
    const areaUnavailableNotice = this.areaUnavailableNotice();

    if (!this.hasInitializedAreaUnavailableNotice) {
      this.hasInitializedAreaUnavailableNotice = true;
      return;
    }

    if (!areaUnavailableNotice) {
      return;
    }

    if (this.startResourceDetail.hasValue()) {
      const result = this.startResourceDetail.value().areaId === areaUnavailableNotice.invalidAreaId;
      if (result) {
        this.startResource.set({
          resourceId: null,
          qrCodeId: null,
        });
      }
    }

    if (this.targetResourceDetail.hasValue()) {
      const result = this.targetResourceDetail.value().areaId === areaUnavailableNotice.invalidAreaId;
      if (result) {
        this.targetResource.set({
          resourceId: null,
          qrCodeId: null,
        });
      }
    }
  });

  protected routeUiState = computed(() => {
    const routeFindStatus = this.routeFindStatus();
    const routeSearchResult = this.routeSearchResult();

    if (routeFindStatus === RouteStateEnum.StartNotFound) {
      return {
        startError: true,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.startNotFound,
      };
    }

    if (routeFindStatus === RouteStateEnum.TargetNotFound) {
      return {
        startError: false,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.targetNotFound,
      };
    }

    const startRequested = routeSearchResult?.start !== StartTargetResultEnum.NotRequested;
    const targetRequested = routeSearchResult?.target !== StartTargetResultEnum.NotRequested;
    const sameStartTarget =
      routeFindStatus === RouteStateEnum.SameStartTarget ||
      (routeSearchResult?.path === PathInfoResultEnum.SameStartAsTarget && startRequested && targetRequested);

    if (sameStartTarget) {
      return {
        startError: true,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.sameStartAsTarget,
      };
    }

    if (routeSearchResult == null) {
      return {
        startError: false,
        targetError: false,
        routeError: false,
        message: '',
      };
    }

    if (
      routeSearchResult.target === StartTargetResultEnum.NotFound &&
      routeSearchResult.start === StartTargetResultEnum.NotFound
    ) {
      return {
        startError: true,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.startAndTargetNotFound,
      };
    }

    if (routeSearchResult.target === StartTargetResultEnum.NotFound) {
      return {
        startError: false,
        targetError: true,
        routeError: false,
        message: translation.wayfinding.targetNotFound,
      };
    }

    if (routeSearchResult.start === StartTargetResultEnum.NotFound) {
      return {
        startError: true,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.startNotFound,
      };
    }

    if (routeSearchResult.path === PathInfoResultEnum.FoundOnlyNotBarrierPath) {
      return {
        startError: false,
        targetError: false,
        routeError: false,
        message: translation.wayfinding.barrierFreenotFound,
      };
    }

    if (routeSearchResult.path === PathInfoResultEnum.PathNotFound && startRequested && targetRequested) {
      return {
        startError: false,
        targetError: false,
        routeError: true,
        message: translation.wayfinding.notFound,
      };
    }

    return {
      startError: false,
      targetError: false,
      routeError: false,
      message: '',
    };
  });

  protected startInputError = computed(() => this.routeUiState().startError);
  protected targetInputError = computed(() => this.routeUiState().targetError);
  protected routeErrorMessage = computed(() => this.routeUiState().message);
  protected routeConnectorError = computed(() => this.routeUiState().routeError);
  protected showRouteOverview = computed(() => (this.navBarData()?.areaList.length ?? 0) > 0);

  startResourceDetailParams = computed(
    () => ({
      resourceId: this.startResource()?.resourceId,
      qrCodeId: this.startResource()?.qrCodeId,
    }),
    {
      equal: (a, b) => a.resourceId === b.resourceId && a.qrCodeId === b.qrCodeId,
    }
  );

  startResourceDetail = rxResource({
    params: () => (this.hasStart() ? this.startResourceDetailParams() : undefined),
    stream: ({ params }) =>
      this.navigationService.getNavigationDetail({
        resourceId: params.resourceId,
        qrCodeId: params.qrCodeId,
        appId: null,
        kioskId: null,
      }),
  });

  targetResourceDetailParams = computed(
    () => ({
      resourceId: this.targetResource()?.resourceId,
      qrCodeId: this.targetResource()?.qrCodeId,
    }),
    {
      equal: (a, b) => a.resourceId === b.resourceId && a.qrCodeId === b.qrCodeId,
    }
  );

  targetResourceDetail = rxResource({
    params: () => (this.hasTarget() ? this.targetResourceDetailParams() : undefined),
    stream: ({ params }) =>
      this.navigationService.getNavigationDetail({
        resourceId: params.resourceId,
        qrCodeId: params.qrCodeId,
        appId: null,
        kioskId: null,
      }),
  });

  protected deleteStart(event: MouseEvent) {
    this.requestDeleteStart.emit();
    event.stopPropagation();
  }

  protected deleteTarget(event: MouseEvent) {
    this.requestDeleteTarget.emit();
    event.stopPropagation();
  }

  openMapItemDetail(data: ResourcePoiIdentificationCrate, isStart: boolean) {
    this.requestOpenMapItemDetail.emit({
      ...data,
      openDetailFrom: isStart ? OpenDetailFromEnum.StartSelection : OpenDetailFromEnum.TargetSelection,
    });
  }
}
